'use client';

import { useState } from 'react';
import { LayoutGrid, List, MessageSquare, ChevronRight, ChevronDown, Link2 } from 'lucide-react';
import { issues, flattenIssues, Issue } from './mockData';

type ViewMode = 'kanban' | 'tree';

export function IssuesTab() {
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');

  return (
    <div className="p-4">
      {/* View Toggle */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-text">Issue Tracker</h3>
        <div className="flex items-center gap-1 p-1 bg-bg-card rounded-lg border border-border/50">
          <ViewToggleButton
            icon={<LayoutGrid className="w-4 h-4" />}
            label="Kanban"
            active={viewMode === 'kanban'}
            onClick={() => setViewMode('kanban')}
          />
          <ViewToggleButton
            icon={<List className="w-4 h-4" />}
            label="Tree"
            active={viewMode === 'tree'}
            onClick={() => setViewMode('tree')}
          />
        </div>
      </div>

      {/* Content */}
      {viewMode === 'kanban' ? <KanbanView /> : <TreeView />}
    </div>
  );
}

interface ViewToggleButtonProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

function ViewToggleButton({ icon, label, active, onClick }: ViewToggleButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
        active
          ? 'bg-primary/20 text-primary'
          : 'text-text-muted hover:text-text-secondary hover:bg-white/5'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// Kanban View Component
function KanbanView() {
  const flatIssues = flattenIssues(issues);

  const columns = [
    { status: 'Blocked', color: 'red', borderColor: 'border-red-500/30', bgColor: 'bg-red-500/10' },
    { status: 'Ready', color: 'green', borderColor: 'border-green-500/30', bgColor: 'bg-green-500/10' },
    { status: 'In Progress', color: 'blue', borderColor: 'border-blue-500/30', bgColor: 'bg-blue-500/10' },
    { status: 'Closed', color: 'gray', borderColor: 'border-gray-500/30', bgColor: 'bg-gray-500/10' },
  ] as const;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {columns.map((column) => {
        const columnIssues = flatIssues.filter((issue) => issue.status === column.status);
        return (
          <KanbanColumn
            key={column.status}
            title={column.status}
            issues={columnIssues}
            borderColor={column.borderColor}
            bgColor={column.bgColor}
          />
        );
      })}
    </div>
  );
}

interface KanbanColumnProps {
  title: string;
  issues: Issue[];
  borderColor: string;
  bgColor: string;
}

function KanbanColumn({ title, issues, borderColor, bgColor }: KanbanColumnProps) {
  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} overflow-hidden`}>
      {/* Column Header */}
      <div className="px-3 py-2 border-b border-border/30">
        <div className="flex items-center justify-between">
          <span className="font-medium text-text">{title}</span>
          <span className="text-text-muted text-sm">{issues.length}</span>
        </div>
      </div>

      {/* Cards */}
      <div className="p-2 space-y-2 max-h-80 overflow-y-auto">
        {issues.map((issue) => (
          <IssueCard key={issue.id} issue={issue} compact />
        ))}
        {issues.length === 0 && (
          <div className="text-center text-text-muted text-sm py-4">No issues</div>
        )}
      </div>
    </div>
  );
}

// Tree View Component
function TreeView() {
  return (
    <div className="bg-bg-card rounded-lg border border-border/50 overflow-hidden">
      <div className="p-2 space-y-1">
        {issues.map((issue) => (
          <TreeNode key={issue.id} issue={issue} depth={0} />
        ))}
      </div>
    </div>
  );
}

interface TreeNodeProps {
  issue: Issue;
  depth: number;
}

function TreeNode({ issue, depth }: TreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = issue.children && issue.children.length > 0;

  return (
    <div>
      <div
        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5 cursor-pointer group"
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        {/* Expand/Collapse */}
        {hasChildren ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-0.5 hover:bg-white/10 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-text-muted" />
            ) : (
              <ChevronRight className="w-4 h-4 text-text-muted" />
            )}
          </button>
        ) : (
          <div className="w-5" />
        )}

        {/* Type Badge */}
        <TypeBadge type={issue.type} />

        {/* Priority Badge */}
        <PriorityBadge priority={issue.priority} />

        {/* Title */}
        <span className="flex-1 text-sm text-text truncate">{issue.title}</span>

        {/* Dependencies indicator */}
        {(issue.blockedBy?.length || issue.blocks?.length) && (
          <Link2 className="w-3.5 h-3.5 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
        )}

        {/* Comments */}
        {issue.comments !== undefined && issue.comments > 0 && (
          <div className="flex items-center gap-1 text-text-muted text-xs">
            <MessageSquare className="w-3.5 h-3.5" />
            <span>{issue.comments}</span>
          </div>
        )}

        {/* Status Badge */}
        <StatusBadge status={issue.status} />

        {/* Issue ID */}
        <span className="text-text-muted text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity">
          {issue.id}
        </span>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {issue.children!.map((child) => (
            <TreeNode key={child.id} issue={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// Shared Issue Card Component
interface IssueCardProps {
  issue: Issue;
  compact?: boolean;
}

function IssueCard({ issue, compact = false }: IssueCardProps) {
  return (
    <div className="p-2 bg-bg-card/80 rounded-md border border-border/30 hover:border-border/50 transition-colors cursor-pointer">
      {/* Header Row */}
      <div className="flex items-center gap-2 mb-1.5">
        <TypeBadge type={issue.type} />
        <PriorityBadge priority={issue.priority} />
        <span className="text-text-muted text-xs font-mono">{issue.id}</span>
      </div>

      {/* Title */}
      <div className="text-sm text-text mb-2 line-clamp-2">{issue.title}</div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        {/* Labels */}
        {!compact && issue.labels.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            {issue.labels.slice(0, 2).map((label) => (
              <span
                key={label}
                className="px-1.5 py-0.5 text-xs bg-white/5 text-text-muted rounded"
              >
                {label.split(':')[1] || label}
              </span>
            ))}
          </div>
        )}

        {/* Comments */}
        {issue.comments !== undefined && issue.comments > 0 && (
          <div className="flex items-center gap-1 text-text-muted text-xs ml-auto">
            <MessageSquare className="w-3 h-3" />
            <span>{issue.comments}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Badge Components
function TypeBadge({ type }: { type: Issue['type'] }) {
  const config = {
    Epic: { label: 'E', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    Feature: { label: 'F', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
    Task: { label: 'T', color: 'bg-white/10 text-text-secondary border-border/30' },
  };

  const { label, color } = config[type];

  return (
    <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-mono rounded border ${color}`}>
      {label}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: Issue['priority'] }) {
  const config = {
    P0: 'bg-red-500/20 text-red-400 border-red-500/30',
    P1: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    P2: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    P3: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    P4: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  return (
    <span className={`inline-flex items-center justify-center px-1.5 h-5 text-xs font-mono rounded border ${config[priority]}`}>
      {priority}
    </span>
  );
}

function StatusBadge({ status }: { status: Issue['status'] }) {
  const config = {
    Blocked: 'bg-red-500/20 text-red-400',
    Ready: 'bg-green-500/20 text-green-400',
    'In Progress': 'bg-blue-500/20 text-blue-400',
    Closed: 'bg-gray-500/20 text-gray-400',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded ${config[status]}`}>
      {status}
    </span>
  );
}
